import {Condition, ReportDefinitionV1} from './contracts/report-definition.types';
import {DatePresetToken, findRelativeDatePreset} from './relativeDatePresets';
import {ReportGrain} from './reportGrain';

export type DateScope = NonNullable<ReportDefinitionV1['dateScope']>;

/** The date fields each grain filters a period on.
 *
 * The two grains do not share a vocabulary, and using the task one on a project report does not
 * fail loudly -- every name below passes the filter-field enum, so a wrong one is accepted and then
 * silently ignored by the query builder, leaving the report unfiltered while its toolbar still
 * names a period.
 *
 * Task: `START_DATE`/`END_DATE` are the inherited dates (resolved through the project hierarchy);
 * `OWN_START_DATE`/`OWN_DEADLINE` are the task's own columns, which is what makes "Deadlines only"
 * mean what its label says.
 *
 * Project status: the project taxonomy publishes `START_DATE` and `DEADLINE`, and those are the
 * only two the project query builder implements date branches for. A project has no separate
 * "own" date, so both narrow scopes bound the same field the overlap uses. */
interface PeriodFields {
	/** The field carrying the window's END under overlap matching. */
	overlapStart: string;
	/** The field carrying the window's START under overlap matching. */
	overlapEnd: string;
	/** The field each narrow scope bounds from both sides. */
	ownFieldByScope: Record<Exclude<DateScope, 'all_dates'>, string>;
	context: Condition['context'];
}

const PERIOD_FIELDS_BY_GRAIN: Record<ReportGrain, PeriodFields> = {
	TASK_REPORT: {
		overlapStart: 'START_DATE',
		overlapEnd: 'END_DATE',
		ownFieldByScope: {start_date: 'OWN_START_DATE', deadline: 'OWN_DEADLINE'},
		context: 'TASKS',
	},
	PROJECT_STATUS: {
		overlapStart: 'START_DATE',
		overlapEnd: 'DEADLINE',
		ownFieldByScope: {start_date: 'START_DATE', deadline: 'DEADLINE'},
		context: 'PROJECTS',
	},
	// An audit event is a point in time, not an interval, so overlap collapses: both bounds are the
	// same field, and the scopes have nothing narrower to offer. AUDIT_EVENT_DATE is the event's own
	// timestamp -- the window a reader means by "what happened in July". The registration's work date
	// is a separate filter, deliberately not this one: they answer different questions, and edits
	// made in July to June's timesheets are exactly the anomaly worth being able to ask for.
	TIME_REG_AUDIT_LOG: {
		overlapStart: 'AUDIT_EVENT_DATE',
		overlapEnd: 'AUDIT_EVENT_DATE',
		ownFieldByScope: {start_date: 'AUDIT_EVENT_DATE', deadline: 'AUDIT_EVENT_DATE'},
		context: 'TIME_REGS',
	},
};

/** An unresolved grain keeps the task fields: it is what every definition predating the grain
 * concept means, and it is the behaviour this function had before grains existed. */
const periodFieldsFor = (grain: ReportGrain | null): PeriodFields => PERIOD_FIELDS_BY_GRAIN[grain ?? 'TASK_REPORT'];

/** Every field any scope on any grain can own, so switching scope OR grain clears the previous
 * one's conditions rather than ANDing two incompatible date windows together.
 *
 * DERIVED from the record above, never restated. It was a hand-written literal, and the moment a
 * grain arrived whose period field was not on that list the omission was silent and destructive:
 * `withPeriodFilterConditions` stopped stripping the previous window, so picking July and then
 * August left `>= Jul 1 AND <= Jul 31 AND >= Aug 1 AND <= Aug 31` and the report went empty. With a
 * relative preset it emptied itself on the first day of a new month, with no edit by anyone. And
 * because `periodOwnedFields` hid the same field from the conditions editor, the leftover
 * conditions were invisible and undeletable -- a report the reader could neither fix nor explain. */
const SCOPED_FIELDS: readonly string[] = Array.from(
	new Set(
		Object.values(PERIOD_FIELDS_BY_GRAIN).flatMap(fields => [
			fields.overlapStart,
			fields.overlapEnd,
			...Object.values(fields.ownFieldByScope),
		])
	)
);

/** The filter fields the top-bar date control OWNS for a grain — everything
 * `withPeriodFilterConditions` can write, and therefore everything it also strips before
 * rewriting.
 *
 * Exported because the conditions editor has to exclude exactly this set from its tile list, and
 * the two were previously two hand-written literals kept in step by hand. They drifted the moment
 * the project grain started writing `DEADLINE`: the editor's copy never gained it, so the period's
 * own bound rendered as a stray condition tile — the duplicate, un-editable date tile that the
 * exclusion exists to prevent.
 *
 * Grain-aware rather than a union of every field, and that distinction is load-bearing. `DEADLINE`
 * is a period field ONLY on a project report; on a task report the period control uses
 * `OWN_DEADLINE`, and `task.dates.deadline` is an ordinary filterable field a reader may hold a
 * real condition on. Excluding `DEADLINE` for every grain would hide that task condition in a tile
 * nobody can see or delete. */
export function periodOwnedFields(grain: ReportGrain | null): ReadonlySet<string> {
	const fields = periodFieldsFor(grain);
	return new Set([fields.overlapStart, fields.overlapEnd, ...Object.values(fields.ownFieldByScope)]);
}

/** Rewrites a definition's date-range filter conditions to match a period and a scope.
 *
 * `all_dates` (the default, and what every report saved before the scope selector existed means) matches by OVERLAP
 * on the inherited dates: a task counts when it starts on or before the period ends and finishes on
 * or after the period begins. Filtering for tasks fully inside the window would hide exactly the
 * long-running work a period report is usually about.
 *
 * `start_date` and `deadline` instead bound one own-date field from both sides. An open start or end
 * simply drops its own condition; with both open nothing is emitted at all, because a scope modifies
 * a range and there is no range to modify.
 *
 * Shared between a manual date-picker edit, a preset selection, a scope change, and recomputing a
 * hydrated preset's period on load, so all four stay consistent. */
export function withPeriodFilterConditions(
	filterConditions: Condition[],
	startDate: string | null,
	endDate: string | null,
	scope: DateScope = 'all_dates',
	grain: ReportGrain | null = 'TASK_REPORT'
): Condition[] {
	const fields = periodFieldsFor(grain);
	const rest = filterConditions.filter(condition => !SCOPED_FIELDS.includes(condition.field));

	if (scope === 'all_dates') {
		if (endDate) {
			rest.push({field: fields.overlapStart, operator: 'LESS_OR_EQUAL', values: [endDate], context: fields.context});
		}
		if (startDate) {
			rest.push({field: fields.overlapEnd, operator: 'GREATER_OR_EQUAL', values: [startDate], context: fields.context});
		}
		return rest;
	}

	const field = fields.ownFieldByScope[scope];
	if (startDate) {
		rest.push({field, operator: 'GREATER_OR_EQUAL', values: [startDate], context: fields.context});
	}
	if (endDate) {
		rest.push({field, operator: 'LESS_OR_EQUAL', values: [endDate], context: fields.context});
	}
	return rest;
}

/** Reads the period back out of a definition's date-range conditions — the inverse of
 * `withPeriodFilterConditions`, and the only place that knows the window is recoverable from the
 * conditions at all.
 *
 * The all-dates arms look transposed and are not: overlap puts the window's END on `START_DATE`
 * (task starts on or before the period ends) and the window's START on `END_DATE`, so reading the
 * window back reverses the same pairing.
 *
 * `operator` narrows to a specific condition when a field carries both bounds at once: an own-date
 * scope puts GREATER_OR_EQUAL and LESS_OR_EQUAL on the SAME field, so matching on field alone would
 * return the start bound for both ends of the window.
 */
export function periodFromConditions(filterConditions: Condition[]): {
	startDate: string | null;
	endDate: string | null;
} {
	// Grain-agnostic, and deliberately matched on OPERATOR rather than field name.
	//
	// Every grain-and-scope combination writes the window's START as the GREATER_OR_EQUAL bound and
	// its END as the LESS_OR_EQUAL bound, whichever field carries it -- so reading the pair back that
	// way is exact for all of them, including the two cases where one field carries both bounds
	// (a task own-date scope, and either narrow scope on a project report). Matching on field name
	// instead cannot separate those two bounds, and cannot tell a project report's `START_DATE`
	// window-end under overlap from its `START_DATE` window-start under the start-date scope.
	//
	// Restricted to SCOPED_FIELDS because those are the only fields this module writes, and the
	// writer strips them all before re-emitting -- so nothing else can be mistaken for a period.
	const bound = (operator: Condition['operator']): string | null =>
		filterConditions.find(condition => SCOPED_FIELDS.includes(condition.field) && condition.operator === operator)
			?.values?.[0] ?? null;

	return {
		startDate: bound('GREATER_OR_EQUAL'),
		endDate: bound('LESS_OR_EQUAL'),
	};
}

/**
 * The window a report is actually looking at: the definition's own dates, falling back to the dates
 * its period filter conditions carry.
 *
 * Two consumers must agree on this, and used to derive it separately: the date control that SHOWS a
 * reader the range, and the fetch that TELLS report-service which moment a row's time-dependent
 * values describe. While the fetch sent nothing, a project-status row reported whichever retainer
 * period contained today, under a header naming a different month entirely.
 *
 * The fallback to the conditions is not a nicety. A report saved before the period was editable
 * carries no `startDate`/`endDate` of its own, and its window exists only as the filter conditions
 * `withPeriodFilterConditions` wrote - so reading the raw fields alone would leave exactly those
 * reports showing one window and reporting another.
 *
 * Returns strings as stored, ISO `yyyy-mm-dd`, and does not resolve a relative `datePreset`: by the
 * time either consumer runs, both the saved-report reload path and the fresh-intent path have
 * already resolved a preset into these fields.
 */
export function effectiveReportPeriod(definition: ReportDefinitionV1): {
	startDate: string | null;
	endDate: string | null;
} {
	const fromConditions = periodFromConditions(definition.filter.filterConditions);
	return {
		startDate: definition.startDate ?? fromConditions.startDate,
		endDate: definition.endDate ?? fromConditions.endDate,
	};
}

/** The period-shaped fields `resolveDatePeriod` reads from a definition. Structurally satisfied by
 * `ReportDefinitionV1` itself, so a caller can pass a whole definition straight through. */
export interface DatePeriodFields {
	datePreset?: string;
	startDate?: string;
	endDate?: string;
	dateScope?: DateScope;
}

/** Whether a definition's literal `startDate`/`endDate` may rebuild its filter conditions.
 *
 * `'always'` is for a freshly drafted definition that has never been saved. It states a range and a
 * scope and carries no conditions for them at all, so the conditions must be derived here or the
 * very first fetch runs unfiltered.
 *
 * `'preset-only'` is for a definition read back from storage, where the persisted conditions are
 * authoritative and must NOT be recomputed from the stored dates. Saving backfills
 * `startDate`/`endDate` with the current month whenever a definition carries none, so a stored
 * report whose real period lives only in its conditions would otherwise have them silently
 * rewritten to that backfilled month on every reopen, and then persisted on the next save. A
 * recognized relative preset still rebuilds under this mode, because rolling the window forward is
 * the entire reason a token is stored instead of dates. */
export type LiteralDateHandling = 'always' | 'preset-only';

/** What `resolveDatePeriod` resolves a definition's period down to: the date fields as they should
 * end up, and the filter conditions that express them. */
export interface ResolvedDatePeriod {
	datePreset?: DatePresetToken;
	startDate?: string;
	endDate?: string;
	filterConditions: Condition[];
}

/** Resolves a definition's period fields into a consistent state together with the filter
 * conditions that express it - the one derivation every path that hydrates a definition from
 * outside the builder's own edit actions shares, so a stored report being reloaded and a
 * chat-drafted intent being opened for the first time cannot silently diverge from each other.
 *
 * A recognized `datePreset` is re-resolved against `today`, overwriting whatever dates it was last
 * resolved to - a stored report's relative window rolls forward, and an intent's preset is resolved
 * for the first time. An unrecognized token is dropped and treated the same as no preset at all,
 * keeping whatever literal `startDate`/`endDate` came with the definition.
 *
 * Either way, once a top-level start and/or end date exists (given directly, or produced by
 * resolving the preset), the conditions are rebuilt from it via `withPeriodFilterConditions`, which
 * fully replaces the four date fields on every call. That makes this safe whether the incoming
 * conditions already match (a reload - the rebuild reproduces them), are entirely absent (a fresh
 * intent - the agent that drafts one states a range and a scope but never conditions for them), or
 * are stale.
 *
 * A definition with neither a preset nor a top-level date is left completely alone: its whole range
 * may already live only in its filter conditions (an older shape some intents still use to express
 * an own-date scope), and there is nothing here that would improve on that.
 *
 * `literalDates` decides whether a literal `startDate`/`endDate` may rebuild the conditions, and the
 * two callers genuinely need different answers - see {@link LiteralDateHandling}. */
export function resolveDatePeriod(
	fields: DatePeriodFields,
	filterConditions: Condition[],
	literalDates: LiteralDateHandling,
	today: Date = new Date(),
	grain: ReportGrain | null = 'TASK_REPORT'
): ResolvedDatePeriod {
	const preset = typeof fields.datePreset === 'string' ? findRelativeDatePreset(fields.datePreset) : undefined;

	if (preset) {
		const resolved = preset.resolve(today);
		return {
			datePreset: preset.token,
			startDate: resolved.startDate,
			endDate: resolved.endDate,
			filterConditions: withPeriodFilterConditions(
				filterConditions,
				resolved.startDate,
				resolved.endDate,
				fields.dateScope,
				grain
			),
		};
	}

	// No preset resolved. Under 'preset-only' the incoming conditions stand as they are: they came
	// from storage and are the authoritative record of the period, which the stored dates may not
	// agree with.
	if (literalDates === 'preset-only' || (fields.startDate === undefined && fields.endDate === undefined)) {
		return {startDate: fields.startDate, endDate: fields.endDate, filterConditions};
	}

	return {
		startDate: fields.startDate,
		endDate: fields.endDate,
		filterConditions: withPeriodFilterConditions(
			filterConditions,
			fields.startDate ?? null,
			fields.endDate ?? null,
			fields.dateScope,
			grain
		),
	};
}
